import React from "react";

/**
 * Event telltales, drawn the way a dashboard warning light would be.
 *
 * One glyph per event kind so a director scanning the log reads shape before text — the same
 * pictograms the cab screen uses for the same events.
 */

type IconProps = { size?: number; className?: string };

const base = (size: number) => ({
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none" as const,
    stroke: "currentColor",
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
});

/** Car with impact arcs above it — a vehicle closing on the attenuator. */
export const CollisionIcon: React.FC<IconProps> = ({ size = 15, className }) => (
    <svg {...base(size)} strokeWidth={1.8} className={className}>
        <path d="M4.6 20v1.4M19.4 20v1.4" />
        <path d="M4 20h16v-4.2a2 2 0 00-.15-.76l-1.5-3.6A2 2 0 0016.5 10.2h-9a2 2 0 00-1.85 1.24l-1.5 3.6A2 2 0 004 15.8V20z" />
        <path d="M7.4 16.6h.01M16.6 16.6h.01" />
        <path d="M8.4 6.6a5 5 0 017.2 0M10.6 3.9a8.4 8.4 0 012.8 0" />
    </svg>
);

/** Speedometer with the needle past the mark. */
export const SpeedingIcon: React.FC<IconProps> = ({ size = 15, className }) => (
    <svg {...base(size)} strokeWidth={1.8} className={className}>
        <path d="M3.6 17a9 9 0 1116.8 0" />
        <path d="M12 13.6l4.2-4.2" />
        <circle cx="12" cy="15.4" r="1.7" />
        <path d="M4.8 12.4h.01M7.6 8.1h.01M12 6.4h.01M16.4 8.1h.01M19.2 12.4h.01" />
    </svg>
);

/** Traffic passing up the left flank of the truck. */
export const LeftPassIcon: React.FC<IconProps> = ({ size = 15, className }) => (
    <svg {...base(size)} strokeWidth={1.9} className={className}>
        <path d="M15 21V10a4 4 0 00-4-4H6" />
        <path d="M9 3L6 6l3 3" />
    </svg>
);

/** Traffic passing up the right flank of the truck. */
export const RightPassIcon: React.FC<IconProps> = ({ size = 15, className }) => (
    <svg {...base(size)} strokeWidth={1.9} className={className}>
        <path d="M9 21V10a4 4 0 014-4h5" />
        <path d="M15 3l3 3-3 3" />
    </svg>
);

export const SearchIcon: React.FC<IconProps> = ({ size = 19, className }) => (
    <svg {...base(size)} strokeWidth={2} className={className}>
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-3.6-3.6" />
    </svg>
);

/** Collapses the sidebar to its icon rail. */
export const PanelLeftIcon: React.FC<IconProps> = ({ size = 19, className }) => (
    <svg {...base(size)} strokeWidth={1.9} className={className}>
        <rect x="3" y="4" width="18" height="16" rx="2.5" />
        <path d="M9.5 4v16" />
    </svg>
);

export const PlayIcon: React.FC<IconProps> = ({ size = 26, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
        <path d="M8 5.5v13l11-6.5z" />
    </svg>
);

export const PlusIcon: React.FC<IconProps> = ({ size = 16, className }) => (
    <svg {...base(size)} strokeWidth={2.2} className={className}>
        <path d="M12 5v14M5 12h14" />
    </svg>
);

export const LogoutIcon: React.FC<IconProps> = ({ size = 16, className }) => (
    <svg {...base(size)} strokeWidth={1.9} className={className}>
        <path d="M15 17l5-5-5-5M20 12H9M12 20H6a2 2 0 01-2-2V6a2 2 0 012-2h6" />
    </svg>
);
